import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v3Api } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import EventList from './EventList';
import EventEditor from './EventEditor';
import EventComparisonChart from './EventComparisonChart';
import './seasonal.css';

const METRICS = [
    { value: 'net_sales',    label: 'Net Sales' },
    { value: 'gross_sales',  label: 'Gross Sales' },
    { value: 'transactions', label: 'Transactions' },
    { value: 'atv',          label: 'ATV' },
];

/**
 * Top-level page for /v3/seasonal.
 * Two tabs: Events (CRUD) | Compare (chart).
 */
export default function SeasonalLandingPage() {
    const { t } = useTranslation();
    const { concessionaireId, propertyAccess } = useAuth();
    const [tab, setTab] = useState('events');

    // Events tab state
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    // Compare tab state
    const [selectedKey, setSelectedKey] = useState('');
    const [metric, setMetric] = useState('net_sales');

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await v3Api.seasonal.list({});
            const list = res?.data || [];
            setEvents(list);
            if (!selectedKey && list.length) setSelectedKey(list[0].event_key);
        } catch (err) {
            setError(err?.response?.data?.error || err?.message || t('Failed to load events'));
        } finally {
            setLoading(false);
        }
    }, [selectedKey, t]);

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

    const openCreate = useCallback(() => { setEditing(null); setEditorOpen(true); }, []);
    const openEdit = useCallback((ev) => { setEditing(ev); setEditorOpen(true); }, []);
    const closeEditor = useCallback(() => { setEditorOpen(false); setEditing(null); }, []);

    const selectedEvent = useMemo(
        () => events.find(e => e.event_key === selectedKey) || null,
        [events, selectedKey],
    );

    return (
        <div className="v3 v3-seasonal-page">
            <div className="v3-seasonal-header">
                <h1 className="v3-heading" style={{ margin: 0 }}>{t('Seasonal & Event Analytics')}</h1>
                {tab === 'events' && (
                    <button className="v3-btn v3-btn-primary" onClick={openCreate}>{t('+ Create event')}</button>
                )}
            </div>

            <div className="v3-seasonal-tabs">
                <button
                    className={`v3-seasonal-tab ${tab === 'events' ? 'active' : ''}`}
                    onClick={() => setTab('events')}
                >{t('Events')}</button>
                <button
                    className={`v3-seasonal-tab ${tab === 'compare' ? 'active' : ''}`}
                    onClick={() => setTab('compare')}
                >{t('Compare')}</button>
            </div>

            {tab === 'events' && (
                <EventList
                    events={events}
                    loading={loading}
                    error={error}
                    onEdit={openEdit}
                    onCreate={openCreate}
                    onChanged={refresh}
                />
            )}

            {tab === 'compare' && (
                <div>
                    <div className="v3-seasonal-compare-controls">
                        <div>
                            <label className="v3-label" style={{ display: 'block', marginBottom: 4 }}>{t('Event')}</label>
                            <select
                                className="v3-input"
                                value={selectedKey}
                                onChange={e => setSelectedKey(e.target.value)}
                                style={{
                                    width: '100%', padding: '8px 10px', borderRadius: 6,
                                    background: 'var(--v3-input-bg, rgba(255,255,255,0.04))',
                                    border: '1px solid var(--v3-border)', color: 'var(--v3-text-primary)',
                                }}
                            >
                                {events.map(ev => (
                                    <option key={ev.event_key} value={ev.event_key}>{ev.event_name}</option>
                                ))}
                                {!events.length && <option value="">{t('— no events —')}</option>}
                            </select>
                        </div>
                        <div>
                            <label className="v3-label" style={{ display: 'block', marginBottom: 4 }}>{t('Metric')}</label>
                            <select
                                value={metric}
                                onChange={e => setMetric(e.target.value)}
                                style={{
                                    width: '100%', padding: '8px 10px', borderRadius: 6,
                                    background: 'var(--v3-input-bg, rgba(255,255,255,0.04))',
                                    border: '1px solid var(--v3-border)', color: 'var(--v3-text-primary)',
                                }}
                            >
                                {METRICS.map(m => <option key={m.value} value={m.value}>{t(m.label)}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="v3-card" style={{ padding: 16, minHeight: 400 }}>
                        {selectedKey ? (
                            <EventComparisonChart
                                eventKey={selectedKey}
                                propertyId={propertyAccess || undefined}
                                metric={metric}
                            />
                        ) : (
                            <div className="v3-body">{t('Select an event to compare year-over-year.')}</div>
                        )}
                    </div>

                    {selectedEvent?.description && (
                        <div className="v3-body" style={{ marginTop: 8, color: 'var(--v3-text-muted)' }}>
                            {selectedEvent.description}
                        </div>
                    )}
                </div>
            )}

            {editorOpen && (
                <EventEditor
                    event={editing}
                    onClose={closeEditor}
                    onSaved={refresh}
                />
            )}

            {/* concessionaireId is available for future scoping (filters etc.) */}
            <input type="hidden" data-concessionaire={concessionaireId} />
        </div>
    );
}
